import moment from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {ROUTES} from '../../constants/routes';
import {AccountContext, AccountContextInterface} from '../../contexts/account-context';
import {useAuth} from '../../contexts/auth.provider';
import {useJobTitle} from '../../contexts/job-title.provider';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {useSkill} from '../../contexts/skill.provider';
import {Resource, ResourceSource} from '../../models';
import {
  CreateOpportunityFormData,
  CreateOpportunityParams,
  Department,
  EducationRequirement,
  EmploymentType,
  ExperienceLevel,
  Opportunity,
  OpportunityStatus,
  OpportunityVisibility,
  UploadStatus,
} from '../../models/opportunity';
import {Community} from '../../models/community';
import {CommunityServiceImpl} from '../../services';

export const useEditOpportunity = () => {
  const navigate = useNavigate();
  const {id} = useParams<{id: string}>();
  const [searchParams] = useSearchParams();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [initialValues, setInitialValues] = useState<CreateOpportunityFormData | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [existingVideoResource, setExistingVideoResource] = useState<Resource | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<CreateOpportunityFormData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UploadStatus.UPLOADING);
  const [isLoading, setIsLoading] = useState(true);

  const {accountAuth, accountService} = useContext<AccountContextInterface>(AccountContext);
  const {user} = useAuth();
  const {createJobTitle} = useJobTitle();
  const {createSkill} = useSkill();
  const {getOpportunityById, updateOpportunity} = useOpportunity();
  const communityService = useMemo(() => new CommunityServiceImpl(), []);

  useEffect(() => {
    const fetchOpportunity = async () => {
      if (!id) {
        navigate(ROUTES.OPPORTUNITIES);
        return;
      }

      try {
        setIsLoading(true);
        const opportunityData = await getOpportunityById(id);

        if (!opportunityData) {
          console.error('Opportunity not found');
          navigate(ROUTES.OPPORTUNITIES);
          return;
        }

        setOpportunity(opportunityData);

        if (opportunityData.videoUrl) {
          setExistingVideoResource(opportunityData.videoUrl);
        }

        const formData: CreateOpportunityFormData = {
          applicationDeadline: moment(opportunityData.applicationDeadline).format('YYYY-MM-DD'),
          department: opportunityData.department,
          educationRequirements: opportunityData.educationRequirements,
          employmentType: opportunityData.employmentType,
          experienceLevel: opportunityData.experienceLevel,
          jobDescription: opportunityData.jobDescription,
          jobTitle: opportunityData.jobTitle,
          location: opportunityData.location,
          requiredSkills: opportunityData.requiredSkills.map(skill => skill.name),
          restrictedToCommunityIds: opportunityData.restrictedToCommunityIds ?? [],
          salary: opportunityData.salary,
          visibility: opportunityData.visibility,
        };

        setInitialValues(formData);

        if (searchParams.get('showPreview') === 'true') {
          setPreviewData(formData);
          setShowPreview(true);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('showPreview');
          navigate(`${window.location.pathname}?${newSearchParams.toString()}`, {replace: true});
        }
      } catch (error) {
        console.error('Error fetching opportunity:', error);
        navigate(ROUTES.OPPORTUNITIES);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOpportunity();
  }, [id, getOpportunityById, navigate, searchParams]);

  useEffect(() => {
    const accountId = accountAuth?.id ?? user?.id;
    if (!accountId) return;

    let cancelled = false;
    const loadDiscoverCommunities = async () => {
      setCommunitiesLoading(true);
      try {
        const limit = 100;
        let page = 1;
        let total = 0;
        const all: Community[] = [];

        do {
          const res = await communityService.discoverCommunities({limit, page});
          if (res.hasError() || !res.data) break;

          all.push(...res.data.data);
          total = res.data.total ?? all.length;
          page += 1;
        } while (all.length < total);

        if (!cancelled) {
          const uniqueCommunities = Array.from(new Map(all.map(c => [c.id, c])).values());
          setCommunities(uniqueCommunities);
        }
      } finally {
        if (!cancelled) setCommunitiesLoading(false);
      }
    };

    loadDiscoverCommunities();
    return () => {
      cancelled = true;
    };
  }, [accountAuth?.id, user?.id, communityService]);

  const handleSubmit = useCallback(async (values: CreateOpportunityFormData) => {
    setPreviewData(values);
    setShowPreview(true);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(ROUTES.OPPORTUNITIES);
  }, [navigate]);

  const handleVideoUpload = useCallback((_fileName: string, file: File) => {
    setVideoFile(file);
    setExistingVideoResource(null);
  }, []);

  const handleBackToEdit = useCallback(() => {
    setShowPreview(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowPreview(false);
  }, []);

  const uploadVideoResource = useCallback(
    async (userId: string): Promise<Resource | undefined> => {
      if (!videoFile) {
        return opportunity?.videoUrl || undefined;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus(UploadStatus.UPLOADING);

      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('source', ResourceSource.CRED);
      formData.append('type', 'video');

      const onUploadProgress = (progressEvent: any) => {
        if (progressEvent.lengthComputable) {
          const uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(uploadPercent);

          if (uploadPercent === 100) {
            setUploadStatus(UploadStatus.PROCESSING);
          }
        }
      };

      try {
        const uploadResponse = await accountService.createReccoResource(userId, formData, onUploadProgress);

        if (uploadResponse.hasError()) {
          throw new Error(uploadResponse.error || 'Failed to upload video');
        }

        if (uploadResponse.data && uploadResponse.data.length > 0) {
          setUploadStatus(UploadStatus.COMPLETE);
          return uploadResponse.data[0]; // Return the full resource object
        }
        return opportunity?.videoUrl || undefined;
      } finally {
        setIsUploading(false);
        setUploadStatus(UploadStatus.UPLOADING);
      }
    },
    [videoFile, opportunity?.videoUrl, accountService],
  );

  const createJobTitleAndSkills = useCallback(async () => {
    if (!previewData) throw new Error('Missing preview data');

    const jobTitleResult = await createJobTitle(previewData.jobTitle);
    if (!jobTitleResult) {
      throw new Error('Failed to create job title');
    }

    const skillPromises = previewData.requiredSkills.map(skillName => createSkill(skillName));
    const skillResults = await Promise.all(skillPromises);
    const skillIds = skillResults.filter(Boolean).map(result => result.id);

    return {jobTitleResult, skillIds};
  }, [previewData, createJobTitle, createSkill]);

  const updateOpportunityWithStatus = useCallback(
    async (status: OpportunityStatus, setLoadingState: (loading: boolean) => void, actionName: string) => {
      if (!previewData || !opportunity || (!accountAuth?.id && !user?.id)) {
        console.error('Missing data or authentication');
        return;
      }

      const userId = accountAuth?.id || user?.id;
      if (!userId) {
        console.error('User ID not available');
        return;
      }

      setLoadingState(true);

      try {
        const videoResource = await uploadVideoResource(userId);
        const {jobTitleResult, skillIds} = await createJobTitleAndSkills();

        const visibility = (previewData.visibility as OpportunityVisibility) ?? OpportunityVisibility.PUBLIC;
        const restrictedToCommunityIds =
          visibility === OpportunityVisibility.PRIVATE && previewData.restrictedToCommunityIds?.length
            ? previewData.restrictedToCommunityIds
            : [];

        const updatePayload: CreateOpportunityParams = {
          applicationDeadline: new Date(previewData.applicationDeadline),
          department: previewData.department as Department,
          educationRequirements: previewData.educationRequirements as EducationRequirement,
          employmentType: previewData.employmentType as EmploymentType,
          experienceLevel: previewData.experienceLevel as ExperienceLevel,
          jobDescription: previewData.jobDescription,
          jobTitle: jobTitleResult.id,
          location: previewData.location,
          requiredSkills: skillIds,
          restrictedToCommunityIds: visibility === OpportunityVisibility.PUBLIC ? undefined : restrictedToCommunityIds,
          salary: previewData.salary,
          status,
          visibility,
        };

        if (videoResource && videoResource.id) {
          updatePayload.videoUrl = videoResource.id;
        }

        const updatedOpportunity = await updateOpportunity(opportunity.id, updatePayload);

        if (!updatedOpportunity) {
          throw new Error(`Failed to ${actionName.toLowerCase()}`);
        }

        navigate(ROUTES.OPPORTUNITIES);
      } catch (error) {
        console.error(`Error ${actionName.toLowerCase()}:`, error);
        alert(`Failed to ${actionName.toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setLoadingState(false);
      }
    },
    [previewData, opportunity, accountAuth, user?.id, uploadVideoResource, createJobTitleAndSkills, updateOpportunity, navigate],
  );

  const handleSaveAsDraft = useCallback(async () => {
    await updateOpportunityWithStatus(OpportunityStatus.DRAFT, setIsSavingDraft, 'Save as draft');
  }, [updateOpportunityWithStatus]);

  const handleUpdate = useCallback(async () => {
    if (!opportunity) return;

    const newStatus = opportunity.status === OpportunityStatus.DRAFT ? OpportunityStatus.PUBLISHED : opportunity.status;
    await updateOpportunityWithStatus(newStatus, setIsUpdating, 'Update');
  }, [updateOpportunityWithStatus, opportunity]);

  const handleRemoveVideo = useCallback(() => {
    setVideoFile(null);
    setExistingVideoResource(null);
  }, []);

  const handleVideoUploadInModal = useCallback((file: File) => {
    setVideoFile(file);
    setExistingVideoResource(null);
  }, []);

  return {
    // State
    communities,
    communitiesLoading,
    opportunity,
    initialValues,
    videoFile,
    existingVideoUrl: existingVideoResource?.url,
    showPreview,
    previewData,
    isUpdating,
    isSavingDraft,
    uploadProgress,
    isUploading,
    uploadStatus,
    isLoading,

    // Handlers
    handleSubmit,
    handleCancel,
    handleVideoUpload,
    handleBackToEdit,
    handleCloseModal,
    handleSaveAsDraft,
    handleUpdate,
    handleRemoveVideo,
    handleVideoUploadInModal,

    // Setters
    setVideoFile,
    setShowPreview,
    setPreviewData,
  };
};
